import { orderBy } from 'lodash';
import {
  IBETARenderer,
  SpecialNeedsValueGetter,
  SpecialNeedsRenderer
} from '@dataGrid/components/cellRenderer';
import {
  getAPTWindowConfig,
  ibEtaValueFormatter,
  SAIADeliveryWindowGetter
} from '../../ShipmentsView/utils';
import { format } from 'date-fns';
import { formatDeliveryWindow, formatESTDateTime } from '@dataGrid/gridUtilities';
import appConstants from '../../../../../appConstants';
import { PRINT_PUBLISH_DATE_FORMAT, PLAN_DATE_REPORT_FORMAT } from './constants';
import { sanitizeInput } from '../../../../../utilities/dataUtils';
import {BarCode, BreakableText, ConsigneeAddress} from './components';

const calculateTotal = (shipments, field) => {
  return shipments.reduce((sum, shipment) => sum + (shipment[field] || 0), 0);
};

const getSpecialNeeds = ({ shipment, aptConfig, specialNeedsOptions, styles }) => {
  return (
    <SpecialNeedsRenderer
      getAPTWindow={aptConfig.valueGetter}
      data={shipment}
      value={SpecialNeedsValueGetter({ data: shipment })}
      context={{ specialNeedsOptions }}
      renderSection={appConstants.SPECIAL_NEEDS_SECTION_TYPE.PRINT_SECTION}
      printStyles={styles}
    />
  );
};

const getShipmentEtaDateTime = (shipment) => {
  return (
    <IBETARenderer
      valueFormatted={ibEtaValueFormatter({ value: shipment.shipmentEtaDateTime })}
      data={shipment}
      showInPrint
    />
  );
};

const formatDeliveryWindowForPrint = (input) => {
  // Add <wbr> after '-' and ',' for word breaking, safely handles empty or invalid input
  if (!input) return '';
  return sanitizeInput(input).replace(/[-,]/g, '$&<wbr>');
}

const getDeliveryWindow = (shipment, columnConfig) => {
  let formattedValue = shipment.deliveryWindow;
 //Commenting below as we need to consider APT window always if available
  // if (columnConfig === COLUMN_CONFIG.SAIA) {
  formattedValue = SAIADeliveryWindowGetter({ data: shipment });
  // }
  return (
    <BreakableText
      text={formatDeliveryWindowForPrint(
        formatDeliveryWindow({ data: shipment, value: formattedValue })
      )}
    />
  );
};

const getFormattedLoadingGuideData = ({
  loadingGuideConfig,
  userProfile,
  specialNeedsOptions,
  columnConfig,
  selectedRoutes,
  shipments
}) => {
  const {
    header: headerConfig,
    table: tableConfig,
    summary: summaryConfig,
    sortOrder
  } = loadingGuideConfig;
  const aptConfig = getAPTWindowConfig(columnConfig);
  const sortedRoutes = applySorting(selectedRoutes, sortOrder);
  return sortedRoutes.map((route) => {
    const matchedRoutePros = shipments.filter(
      (shipment) => shipment.routeId?.toLowerCase() === route.routeId?.toLowerCase()
    );

    return {
      header: getFormattedHeaderData({
        headerConfig,
        route,
        userProfile
      }),
      table: getFormattedTableData({
        tableConfig,
        shipments: applySorting(matchedRoutePros, tableConfig.sortOrder),
        aptConfig,
        columnConfig,
        specialNeedsOptions,
        isTrapRoute: route.isTrapRoute
      }),
      summary: getFormattedFooterData({
        summaryConfig,
        pros: matchedRoutePros
      })
    };
  });
};

const getFormattedHeaderData = ({ headerConfig, route, userProfile }) => {
  return {
    ...headerConfig,
    fields: headerConfig.fields.map((fieldConfig) => {
      return {
        ...fieldConfig,
        value: getHeaderValue({ fieldConfig, route, userProfile })
      };
    })
  };
};

const getFormattedTableData = ({
  tableConfig,
  shipments,
  aptConfig,
  columnConfig,
  specialNeedsOptions,
  isTrapRoute,
}) => {
  const proInfoList = shipments.map((shipment, proIndex) =>
    tableConfig.columns.map((col) => ({
      ...col,
      value: getColumnValue({
        col,
        shipment,
        proIndex,
        aptConfig,
        specialNeedsOptions,
        columnConfig,
        isTrapRoute
      })
    }))
  );
  return {
    tableHeaders: tableConfig.columns,
    pros: proInfoList
  };
};


const getFormattedFooterData = ({ summaryConfig, pros }) => {
  return summaryConfig.fields.map((eachKpi) => ({
    ...eachKpi,
    value: getFooterValue({ aggregation: eachKpi.aggregation, pros, field: eachKpi.field })
  }));
};

const getHeaderValue = ({fieldConfig, route, userProfile}) => {
  if (!fieldConfig) return '';
  const { field, value: placeholderValue, barCodeConfig } = fieldConfig;
  switch (field) {
    case 'terminalCode':
      return userProfile.terminal.code;
    case 'planDate':
      return format(userProfile.planDate, PLAN_DATE_REPORT_FORMAT);
    case 'publishedAt':
      return userProfile.lastPublishedDateTime
        ? formatESTDateTime({
            value: userProfile.lastPublishedDateTime,
            dateTimeFormat: PRINT_PUBLISH_DATE_FORMAT
          })
        : '';
    default:
      if (barCodeConfig) {
        const barCodeValue = route[barCodeConfig.field];
        if (!barCodeValue) return placeholderValue || '';
        return <BarCode {...barCodeConfig} value={barCodeValue} />;
      }
      return route[field] || placeholderValue || '';
  }
};

const getLoadingZoneLabel = ({ isTrapRoute, useCustomLoadingZone, proIndex, shipment, field }) => {
  if (isTrapRoute) return '';
  return useCustomLoadingZone ? `C${proIndex + 1}` : shipment[field];
};

const splitFields = (fields, barcode) => {
  const otherFields = [];
  let barcodeField = null;
  fields.forEach((f) => {
    if (f === barcode) {
      barcodeField = f;
      return;
    }
    otherFields.push(f);
  });
  return { barcodeField, otherFields };
};

const renderFieldsWithSeparator = ({ fields, separator, renderFn }) => {
  const validFields = fields.map((field) => renderFn(field)).filter(Boolean);
  return validFields.map((content, index) => (
    <React.Fragment key={content || index}>
      {index > 0 && separator}
      {content}
    </React.Fragment>
  ));
};

const getColumnValue = ({ col, ...props }) => {
  const { field, fieldCallBackFn, separator, ...otherColProps } = col;
  if (Array.isArray(field)) {
    const { barcodeField, otherFields } = splitFields(field, 'barcode');
    return (
      <div style={otherColProps.styles || {}}>
        <div style={otherColProps.separatorStyles}>
          {renderFieldsWithSeparator({
            fields: otherFields,
            separator,
            renderFn: (individualField) =>
              resolveFieldContent({
                ...props,
                ...otherColProps,
                field: individualField,
                fieldCallBackFn,
              })
          })}
        </div>
        {barcodeField && //render barcode field separately
          resolveFieldContent({
            ...props,
            ...otherColProps,
            field: barcodeField,
            fieldCallBackFn,
          })}
      </div>
    );
  }
  return resolveFieldContent({ ...props, ...otherColProps, field, fieldCallBackFn });
};

const resolveFieldContent = ({
  field,
  fieldCallBackFn,
  shipment,
  proIndex,
  aptConfig,
  specialNeedsOptions,
  columnConfig,
  isTrapRoute,
  showEmptyCell,
  useCustomLoadingZone,
  barCodeConfig,
  styles,
}) => {
  switch (field) {
    case 'barcode': {
      const barCodeField = barCodeConfig?.field;
      const customTextConfig = barCodeConfig?.customTextConfig; // To override the barcode text
      const barCodeProps = {
        ...barCodeConfig,
        value: barCodeField ? shipment[barCodeField] || '' : '',
        ...(customTextConfig && {
          text: customTextConfig.fields
            .filter((config) => shipment[config.field])
            .map((config) => `${config.prefix || ''}${shipment[config.field]}`)
            .join(customTextConfig.separator)
        })
      };
      return <BarCode {...barCodeProps} />;
    }
    case 'consigneeAddress':
    case 'shipperAddress':
      return <ConsigneeAddress data={shipment} fieldCallBackFn={fieldCallBackFn} />;
    case 'shipmentEtaDateTime':
      return getShipmentEtaDateTime(shipment);
    case 'splNeeds':
      return getSpecialNeeds({ shipment, aptConfig, specialNeedsOptions, styles });
    case 'deliveryWindow':
      return getDeliveryWindow(shipment, columnConfig);
    case 'loadingZone':
      return getLoadingZoneLabel({ isTrapRoute, useCustomLoadingZone, proIndex, shipment, field });
    case 'stopSequence':
      return isTrapRoute ? '' : shipment[field];
    default:
      return showEmptyCell ? '' : shipment[field];
  }
};

const getFooterValue = ({ aggregation, pros, field }) => {
  switch (aggregation) {
    case 'count':
      return pros.length;
    case 'sum':
      return calculateTotal(pros, field);
    default:
      return null;
  }
};

const applySorting = (infoList, sortOrder) => {
  const { fields, orders } = sortOrder.reduce(
    (acc, item) => {
      acc.fields.push(item.field);
      acc.orders.push(item.order);
      return acc;
    },
    { fields: [], orders: [] }
  );
  const isValidConfig =
    fields.length > 0 && orders.every((order) => ['asc', 'desc'].includes(order));
  if (!isValidConfig) {
    return infoList;
  }
  return orderBy(infoList, fields, orders);
};

export { getFormattedLoadingGuideData };
